// Admin (super admins): replace blåtur participant lists in one go from pasted JSON.
// Format: { "2017": ["Njaal", "Magnus", ...], "2018": [...] }  (year -> participants; hosts are added automatically)
// Shows a preview per trip before anything is written.

import React, { useMemo, useState } from 'react';
import { getFirestore, doc, updateDoc } from 'firebase/firestore';
import { app } from './firebase-config';
import { tripYear, tripTitle } from './tripHelpers';
import { normalizeName, createPerson } from './adminData';
import { recalculateUserPoints } from './recalculatePoints';
import { BLATUR_TYPE } from './blaturData';

const db = getFirestore(app);

function AdminImport({ user, events, directory, reloadDirectory, onDone }) {
  const [text, setText] = useState('');
  const [createMissing, setCreateMissing] = useState(true);
  const [status, setStatus] = useState('');

  const known = useMemo(() => new Set(directory.names.map(normalizeName)), [directory]);

  const plan = useMemo(() => {
    if (!text.trim()) return null;
    let data;
    try {
      data = JSON.parse(text);
    } catch (error) {
      return { error: 'Ugyldig JSON: ' + error.message };
    }
    const trips = events.filter(e => e.event_type === BLATUR_TYPE);
    const rows = Object.entries(data).map(([year, names]) => {
      const trip = trips.find(t => String(tripYear(t)) === String(year));
      if (!trip) return { year, error: `Fant ingen blåtur i ${year}` };
      const hosts = (trip.Vert || []).map(n => n.trim());
      const next = [...new Set([...names.map(n => n.trim()).filter(Boolean), ...hosts])];
      const current = (trip.Deltagelse || []);
      return {
        year,
        trip,
        next,
        added: next.filter(n => !current.includes(n)),
        removed: current.filter(n => !next.includes(n)),
        unknown: next.filter(n => !known.has(normalizeName(n))),
      };
    });
    return { rows };
  }, [text, events, known]);

  const rows = plan?.rows?.filter(r => !r.error) || [];
  const changes = rows.filter(r => r.added.length || r.removed.length);
  const unknown = [...new Set(rows.flatMap(r => r.unknown))];

  const apply = async () => {
    if (!window.confirm(`Oppdatere deltakerlistene på ${changes.length} turer?`)) return;
    try {
      if (createMissing) {
        for (const name of unknown) {
          setStatus(`Oppretter ${name}…`);
          await createPerson({ name }, user.uid);
        }
      }
      for (const row of changes) {
        setStatus(`Oppdaterer ${row.year}…`);
        await updateDoc(doc(db, 'events', row.trip.id), { Deltagelse: row.next });
      }
      setStatus('Regner ut poeng på nytt…');
      await recalculateUserPoints();
      await reloadDirectory();
      setStatus(`Ferdig: ${changes.length} turer oppdatert.`);
      setText('');
      onDone();
    } catch (error) {
      console.error(error);
      setStatus('Noe gikk galt: ' + error.message);
    }
  };

  return (
    <details className="admin-form admin-import">
      <summary><h2>Importer blåtur-deltakere</h2></summary>
      <p className="admin-hint">
        Lim inn deltakerlister som JSON, med år og navn: {'{ "2017": ["Njaal", "Magnus"], … }'}.
        Listen erstatter dagens deltakere på turen. Arrangørene legges til automatisk.
      </p>
      <div className="admin-field">
        <textarea rows={8} value={text} onChange={(e) => setText(e.target.value)} placeholder='{ "2017": ["Njaal", "Magnus"] }' spellCheck={false} />
      </div>

      {plan?.error && <p className="admin-warn">{plan.error}</p>}
      {plan?.rows?.filter(r => r.error).map(r => <p key={r.year} className="admin-warn">{r.error}</p>)}

      {rows.length > 0 && (
        <>
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead><tr><th>Tur</th><th>Legges til</th><th>Fjernes</th><th>Totalt</th></tr></thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.year} className="static">
                    <td>{r.year} {tripTitle(r.trip)}</td>
                    <td>{r.added.join(', ') || '–'}</td>
                    <td>{r.removed.map(n => `«${n}»`).join(', ') || '–'}</td>
                    <td>{(r.trip.Deltagelse || []).length} → {r.next.length}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {unknown.length > 0 && (
            <label className="admin-hint admin-import-missing">
              <input type="checkbox" checked={createMissing} onChange={(e) => setCreateMissing(e.target.checked)} />
              Opprett {unknown.length} navn uten bruker i personregisteret: {unknown.join(', ')}.
              Har noen av dem en bruker under et annet navn, rett navnet under Personer først.
            </label>
          )}
          <div className="admin-actions">
            <button className="button-17" onClick={apply} disabled={changes.length === 0}>
              Importer {changes.length} turer
            </button>
          </div>
        </>
      )}
      {status && <p className="admin-status">{status}</p>}
    </details>
  );
}

export default AdminImport;
